import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';

type AttendanceStatementPdfProps = {
    data: FormattedStatementForExport;
};

Font.register({
    family: 'Roboto',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

Font.register({
    family: 'MonoSanes',
    src: `${process.env.PUBLIC_URL}/fonts/OpenSans-Bold.ttf`,
    fontWeight: 'bold',
});

Font.register({
    family: 'MonoSanes',
    src: `${process.env.PUBLIC_URL}/fonts/OpenSans-SemiBold.ttf`,
    fontWeight: 'semibold',
});

export type FormattedStatementForExport = {
    labels: {
        employee: string;
        dateFrom: string;
        dateTo: string;
        totalDays: string;
        totalBreaks: string;
        timeInWork: string;
        total: string;
        suspendedHours: string;
        totalHours: string;
        date: string;
        arrival: string;
        departure: string;
        break: string;
        workTime: string;
        absences: string;
        type: string;
        businessDays: string;
    };
    title: string;
    identifier: string;
    dateFrom: string;
    dateTo: string;
    employeeName: string;
    companyLogoUrl?: string;
    shifts: Array<{
        date: string;
        arrival: string;
        departure: string;
        break: string;
        workTime: string;
    }>;
    absences?: Array<{
        type: string;
        dateFrom: string;
        dateTo: string;
        days: number;
    }>;
    totalDays: number;
    totalBreaks: string;
    timeInWork: string;
    suspendedHours: string;
    totalHours: string;
};

// Constants
const MULTI_PAGE_THRESHOLD = 31;
const SHIFTS_PER_PAGE = 40;

// Helper function to calculate page size score
const calculatePageSizeScore = (data: FormattedStatementForExport): number => {
    const numberOfShifts = data.shifts.length;
    const numberOfAbsences = data.absences?.length ?? 0;
    const numberOfAbsenceTypes = Object.keys(Object.fromEntries(data.absences?.map((a) => [a.type, true]) ?? [])).length;
    const absencesPenalty = numberOfAbsences > 0 ? 5 : 0; // Compensate for table header

    return numberOfShifts + numberOfAbsences + numberOfAbsenceTypes + absencesPenalty;
};

// Reusable header component
const PageHeader: React.FC<{ data: FormattedStatementForExport; pageNumber?: number }> = ({ data, pageNumber }) => (
    <View style={styles.header}>
        <View style={{ flex: 1 }}>
            <Text style={styles.heading}>{data.title}</Text>
            <Text style={{ fontSize: 9, marginTop: 5 }}>{data.employeeName}</Text>
            <Text style={{ fontSize: 8, color: '#666666' }}>
                {data.dateFrom} — {data.dateTo}
            </Text>
        </View>
        {data.companyLogoUrl && <Image src={data.companyLogoUrl} style={styles.logo} />}
    </View>
);

// Shifts table component
const ShiftsTable: React.FC<{ data: FormattedStatementForExport; shifts: FormattedStatementForExport['shifts'] }> = ({ data, shifts }) => (
    <>
        <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>{data.labels.date}</Text>
                <Text style={styles.centeredCell}>{data.labels.arrival}</Text>
                <Text style={styles.centeredCell}>{data.labels.departure}</Text>
                <Text style={styles.centeredCell}>{data.labels.break}</Text>
                <Text style={styles.centeredCell}>{data.labels.workTime}</Text>
            </View>

            {shifts.map((shift, index) => (
                <View key={index} style={[styles.tableRow, { fontSize: 8 }]}>
                    <Text style={styles.tableCell}> {shift.date} </Text>
                    <Text style={styles.centeredCell}>{shift.arrival} </Text>
                    <Text style={styles.centeredCell}>{shift.departure}</Text>
                    <Text style={styles.centeredCell}>{shift.break}</Text>
                    <Text style={styles.centeredCell}>{shift.workTime} </Text>
                </View>
            ))}
        </View>
        <View style={styles.divider} />
    </>
);

// Absences table component
const AbsencesTable: React.FC<{
    data: FormattedStatementForExport;
    totalAbsenceDays: number;
}> = ({ data, totalAbsenceDays }) => (
    <>
        <Text style={{ marginBottom: 10, textAlign: 'center', fontWeight: 'bold' }}>{data.labels.absences}</Text>
        <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCell, { flex: 1 }]}>{data.labels.dateFrom}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{data.labels.dateTo}</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>{data.labels.type}</Text>
                <Text style={[styles.centeredCell, { flex: 2 }]}>{`${data.labels.businessDays} (${totalAbsenceDays})`}</Text>
            </View>
            {data.absences?.map((absence, index) => (
                <View key={index} style={[styles.tableRow, { fontSize: 8 }]}>
                    <Text style={[styles.tableCell, { flex: 1 }]}>{absence.dateFrom}</Text>
                    <Text style={[styles.tableCell, { flex: 1 }]}>{absence.dateTo}</Text>
                    <Text style={[styles.tableCell, { flex: 2 }]}>{absence.type}</Text>
                    <Text style={[styles.centeredCell, { flex: 2 }]}>{absence.days}</Text>
                </View>
            ))}
        </View>
        <View style={styles.divider} />
    </>
);

// Totals section component
const TotalsSection: React.FC<{
    data: FormattedStatementForExport;
    absencesByType: Record<string, number>;
}> = ({ data, absencesByType }) => (
    <View>
        <Text style={{ marginBottom: 10, textAlign: 'center', fontWeight: 'bold' }}>{data.labels.total}</Text>
        <View style={styles.table}>
            <View style={[styles.tableRow, { fontSize: 10 }]}>
                <Text style={styles.tableCell}>{data.labels.totalDays}</Text>
                <Text style={styles.tableCell}>{data.totalDays}</Text>
                <Text style={styles.tableCell}>{data.labels.totalBreaks}</Text>
                <Text style={styles.tableCell}>{data.totalBreaks}</Text>
            </View>
            <View style={[styles.tableRow, { fontSize: 10 }]}>
                <Text style={styles.tableCell}>{data.labels.timeInWork}</Text>
                <Text style={styles.tableCell}>{data.timeInWork}</Text>
                <Text style={styles.tableCell}>{data.labels.suspendedHours}</Text>
                <Text style={styles.tableCell}>{data.suspendedHours}</Text>
            </View>
            {Object.keys(absencesByType).length > 0 && (
                <>
                    <Text style={{ marginTop: 10, fontWeight: 'bold' }}>{data.labels.absences}</Text>
                    <View style={[styles.tableRow, { fontWeight: 'bold', fontSize: 8 }]}>
                        <Text style={styles.tableCell}>{data.labels.total}</Text>
                        <Text style={styles.tableCell}>{Object.values(absencesByType).reduce((sum, days) => sum + days, 0)}d</Text>
                        <Text style={styles.tableCell} />
                        <Text style={styles.tableCell} />
                    </View>
                    {Object.entries(absencesByType).map(([type, days], index) => (
                        <View key={index} style={[styles.tableRow, { fontSize: 8 }]}>
                            <Text style={styles.tableCell}>{type}</Text>
                            <Text style={styles.tableCell}>{days}d</Text>
                            <Text style={styles.tableCell} />
                            <Text style={styles.tableCell} />
                        </View>
                    ))}
                </>
            )}
        </View>
        <View
            style={{
                borderWidth: 1,
                borderRadius: 12,
                borderColor: '#2e2e2e',
                padding: 5,
                paddingVertical: 10,
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginHorizontal: 20,
                marginTop: 10,
                fontSize: 12,
                fontWeight: 'bold',
            }}
        >
            <Text>{data.labels.totalHours}</Text>
            <Text>{data.totalHours}</Text>
        </View>
    </View>
);

export const AttendanceStatementPdf: React.FC<AttendanceStatementPdfProps> = ({ data }) => {
    // Calculate absences by type
    const absencesByType: Record<string, number> = {};
    data.absences?.forEach((absence) => {
        absencesByType[absence.type] = (absencesByType[absence.type] ?? 0) + absence.days;
    });
    const totalAbsenceDays = Object.values(absencesByType).reduce((sum, days) => sum + days, 0);

    // Determine if multi-page layout is needed
    const pageScore = calculatePageSizeScore(data);
    const useMultiPageLayout = pageScore > MULTI_PAGE_THRESHOLD;

    if (!useMultiPageLayout) {
        // Single page layout
        return (
            <Document title={'VD.pdf'}>
                <Page size="A4" style={styles.page}>
                    <PageHeader data={data} />

                    {/* Employee Details Table */}
                    {/* ...existing employee details code... */}

                    <View style={styles.divider} />

                    {/* Shifts Table */}
                    <ShiftsTable data={data} shifts={data.shifts} />

                    {/* Absences Table */}
                    {data.absences && data.absences.length > 0 && <AbsencesTable data={data} totalAbsenceDays={totalAbsenceDays} />}

                    {/* Totals Section */}
                    <TotalsSection data={data} absencesByType={absencesByType} />
                </Page>
            </Document>
        );
    }

    // Multi-page layout
    const shiftPages = Math.ceil(data.shifts.length / SHIFTS_PER_PAGE);

    return (
        <Document title={'VD.pdf'}>
            {/* First page: Employee details + first batch of shifts */}
            <Page size="A4" style={styles.page}>
                <PageHeader data={data} pageNumber={1} />

                {/* Employee Details Table */}
                {/* ...existing employee details code... */}

                <View style={styles.divider} />

                {/* First batch of shifts */}
                <ShiftsTable data={data} shifts={data.shifts.slice(0, SHIFTS_PER_PAGE)} />
            </Page>

            {/* Additional pages for remaining shifts */}
            {Array.from({ length: shiftPages - 1 }).map((_, pageIndex) => {
                const pageNumber = pageIndex + 2;
                const startIdx = pageIndex * SHIFTS_PER_PAGE + SHIFTS_PER_PAGE;
                const endIdx = startIdx + SHIFTS_PER_PAGE;

                return (
                    <Page key={pageIndex} size="A4" style={styles.page}>
                        <PageHeader data={data} pageNumber={pageNumber} />
                        <View style={styles.divider} />
                        <ShiftsTable data={data} shifts={data.shifts.slice(startIdx, endIdx)} />
                    </Page>
                );
            })}

            {/* Final page: Absences + Totals */}
            <Page size="A4" style={styles.page}>
                <PageHeader data={data} pageNumber={shiftPages + 1} />
                <View style={styles.divider} />

                {data.absences && data.absences.length > 0 && <AbsencesTable data={data} totalAbsenceDays={totalAbsenceDays} />}

                <TotalsSection data={data} absencesByType={absencesByType} />
            </Page>
        </Document>
    );
};

// ...existing styles...

const styles = StyleSheet.create({
    page: {
        padding: 25,
        fontSize: 10,
        fontWeight: 'semibold',
        fontFamily: 'MonoSanes',
    },
    section: {
        marginBottom: 10,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    heading: {
        fontSize: 16,
        fontWeight: '800',
    },
    logo: {
        maxWidth: 100,
        maxHeight: 50,
        objectFit: 'contain',
    },
    table: {},
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 2,
    },
    tableHeader: {
        fontWeight: 'bold',
        backgroundColor: '#f0f0f0',
        padding: 5,
    },
    tableCell: {
        flex: 1,
    },
    centeredCell: {
        flex: 1,
        textAlign: 'center',
    },
    divider: {
        borderBottomWidth: 0.5,
        borderBottomColor: '#2e2e2e',
        borderBottomStyle: 'solid',
        marginVertical: 10,
    },
});
